import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { usersAPI, peoplePlatformAPI, authAPI, positionsAPI } from '@/services';
import { mapPeopleListResponse, mapPeopleToMemberRow } from '../models/memberModel';
import { useToast } from '../components/Toast';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import {
  normalizeUserRoleForForm,
  formatIamRoleLabel,
  isOrgAdminRole,
  isOrgManagerRole,
  resolveUserRoleFromPosition,
  primaryRoleKeyFromPosition,
} from '../utils/iamRoles';
import { useAssignableRoles } from '../hooks/useAssignableRoles';
import PINInput from '../components/PINInput';
import { staffUsersBasePath, staffUsersListPath } from '../utils/portalAdminNav';

const UserEdit = () => {
  const outletCtx = useOutletContext() || {};
  const profileShellCompte = outletCtx.profileShell === 'compte';
  const { id: routeUserId } = useParams();
  const id = outletCtx.profileUserId ?? routeUserId;
  const navigate = useNavigate();
  const location = useLocation();
  const usersListPath = staffUsersListPath(location.pathname);
  const usersBase = staffUsersBasePath(location.pathname);
  const { error, success } = useToast();
  const { user: currentUser } = useAuth();
  const { role, hasPermission } = usePermissions();
  const { roles: assignableRoles } = useAssignableRoles();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [members, setMembers] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    username: '',
    email: '',
    password: '',
    role: 'participant',
    member_id: '',
    position_id: '',
    is_active: true
  });
  const [projectPositions, setProjectPositions] = useState([]);
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [oldPin, setOldPin] = useState('');
  const [savingPin, setSavingPin] = useState(false);
  const [userHasPin, setUserHasPin] = useState(false);
  
  // L'utilisateur peut modifier son propre compte, ou si c'est un admin
  const isOwnAccount = currentUser?.id === id;
  const canEditRole = hasPermission('users.edit');

  useEffect(() => {
    if (!id) return;
    loadData();
    if (isOwnAccount) {
      checkUserPIN();
    }
  }, [id]);

  useEffect(() => {
    let c = false;
    (async () => {
      try {
        const res = await positionsAPI.getList();
        if (!c) setProjectPositions(res.data?.positions || []);
      } catch {
        if (!c) setProjectPositions([]);
      }
    })();
    return () => {
      c = true;
    };
  }, []);

  const checkUserPIN = async () => {
    try {
      const response = await authAPI.getMe();
      setUserHasPin(!!response.data.has_pin);
    } catch (err) {
      console.warn('Erreur lors de la vérification du PIN:', err);
    }
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [userResponse, membersResponse] = await Promise.all([
        usersAPI.getById(id),
        peoplePlatformAPI.list({ limit: 2000, skip: 0 })
      ]);
      
      const user = userResponse.data;
      
      // Si l'utilisateur a un membre associé, charger l'email du membre pour synchronisation
      let email = user.email || '';
      if (user.member_id) {
        try {
          const memberResponse = await peoplePlatformAPI.getById(user.member_id);
          const row = mapPeopleToMemberRow(memberResponse.data);
          if (row.email) email = row.email;
        } catch (err) {
          console.warn('Person not found or error loading profile:', err);
        }
      }
      
      setFormData({
        name: user.name || '',
        phone: user.phone || '',
        username: user.username || '',
        email: email,
        password: '',
        role: normalizeUserRoleForForm(user.role) || 'participant',
        member_id: user.member_id || '',
        position_id: user.position_id ? String(user.position_id) : '',
        is_active: user.is_active !== undefined ? user.is_active : true
      });
      setMembers(mapPeopleListResponse(membersResponse.data));
    } catch (err) {
      error('Erreur lors du chargement de l\'utilisateur');
      console.error(err);
      navigate(profileShellCompte ? '/profile/compte' : usersListPath);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password; // Don't send empty password
      }
      
      // Si l'utilisateur modifie son propre compte et n'est pas admin, ne permettre la modification que de username, phone, email, password
      if (isOwnAccount && !canEditRole) {
        // Ne garder que username, phone, email, password
        const restrictedUpdateData = {
          username: updateData.username,
          phone: updateData.phone,
          email: updateData.email
        };
        
        // Ajouter le mot de passe s'il est fourni
        if (updateData.password) {
          restrictedUpdateData.password = updateData.password;
        }
        
        // Vérifier qu'au moins un identifiant est fourni
        if (!restrictedUpdateData.phone && !restrictedUpdateData.username && !restrictedUpdateData.email) {
          error('Vous devez fournir au moins un identifiant (téléphone, username ou email)');
          return;
        }
        await usersAPI.update(id, restrictedUpdateData);
        success('Utilisateur mis à jour avec succès');
        navigate(profileShellCompte ? '/profile/compte' : '/profile');
        return;
      }
      
      // Vérifier qu'au moins un identifiant est fourni (pour les admins)
      if (!updateData.phone && !updateData.username && !updateData.email) {
        error('Vous devez fournir au moins un identifiant (téléphone, username ou email)');
        return;
      }
      
      await usersAPI.update(id, updateData);
      success('Utilisateur mis à jour avec succès');
      if (isOwnAccount) {
        navigate(profileShellCompte ? '/profile/compte' : '/profile');
      } else {
        navigate(`${usersBase}/${id}`);
      }
    } catch (err) {
      error(err.response?.data?.detail || 'Erreur lors de la mise à jour');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (isOwnAccount) {
      navigate(profileShellCompte ? '/profile/compte' : '/profile');
    } else {
      navigate(`${usersBase}/${id}`);
    }
  };

  const handlePINComplete = (pinValue, type) => {
    if (type === 'old') {
      setOldPin(pinValue);
    } else if (type === 'new') {
      setPin(pinValue);
    } else if (type === 'confirm') {
      setConfirmPin(pinValue);
    }
  };

  const handleSavePIN = async () => {
    const cleanPin = pin.replace(/\D/g, '');
    const cleanConfirmPin = confirmPin.replace(/\D/g, '');
    const cleanOldPin = oldPin.replace(/\D/g, '');

    if (cleanPin.length !== 6 || cleanConfirmPin.length !== 6) {
      error('Le PIN doit contenir exactement 6 chiffres');
      return;
    }

    if (cleanPin !== cleanConfirmPin) {
      error('Les PIN ne correspondent pas');
      setConfirmPin('');
      return;
    }

    if (userHasPin && cleanOldPin.length !== 6) {
      error('L\'ancien PIN doit contenir 6 chiffres');
      return;
    }

    try {
      setSavingPin(true);
      await authAPI.createPin(cleanPin, userHasPin ? cleanOldPin : null);
      success('PIN créé/modifié avec succès');
      setPin('');
      setConfirmPin('');
      setOldPin('');
      setUserHasPin(true);
    } catch (err) {
      error(err.response?.data?.detail || 'Erreur lors de la sauvegarde du PIN');
      if (err.response?.data?.detail?.includes('ancien')) {
        setOldPin('');
      }
    } finally {
      setSavingPin(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    const r = role != null ? String(role).toLowerCase() : '';
    const colors = {
      admin: 'bg-red-100 text-red-800',
      org_admin: 'bg-red-100 text-red-800',
      organization_admin: 'bg-red-100 text-red-800',
      manager: 'bg-amber-100 text-amber-800',
      org_manager: 'bg-amber-100 text-amber-800',
      executive: 'bg-entrea-panel text-purple-800',
      finance_lead: 'bg-green-100 text-green-800',
      operations_lead: 'bg-blue-100 text-blue-800',
      participant: 'bg-gray-100 text-gray-800',
      président: 'bg-entrea-panel text-purple-800',
      trésorier: 'bg-green-100 text-green-800',
      secrétaire: 'bg-blue-100 text-blue-800',
      membre: 'bg-gray-100 text-gray-800',
      member: 'bg-gray-100 text-gray-800'
    };
    return colors[r] || colors.participant;
  };

  const editingTechnicalIam = useMemo(
    () =>
      Boolean(
        isOrgAdminRole(formData.role) ||
          isOrgManagerRole(formData.role) ||
          ['admin_project', 'saas_admin'].includes(String(formData.role || '').toLowerCase())
      ),
    [formData.role]
  );

  /**
   * Alignement du rôle utilisateur sur la position projet :
   * dès que la position est définie (et que ce n'est pas un compte technique IAM),
   * le rôle reflète le premier `role_keys` de la position.
   */
  useEffect(() => {
    if (editingTechnicalIam) return;
    const pid = String(formData.position_id || '').trim();
    if (!pid) return;
    if (!Array.isArray(projectPositions) || projectPositions.length === 0) return;
    const derived = resolveUserRoleFromPosition(pid, projectPositions, formData.role || 'participant');
    if (!derived) return;
    if (String(derived).toLowerCase() === String(formData.role || '').toLowerCase()) return;
    setFormData((prev) => ({ ...prev, role: derived }));
  }, [formData.position_id, projectPositions, editingTechnicalIam]);

  if (!id) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <p className="text-gray-600">Compte introuvable</p>
          <button
            type="button"
            onClick={() => navigate(profileShellCompte ? '/profile/compte' : usersListPath)}
            className="mt-4 inline-flex min-h-[44px] items-center justify-center rounded-lg bg-entrea-royal px-4 py-2 text-white hover:bg-entrea-navy"
          >
            Retour au profil
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-entrea-royal mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Sticky on mobile */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 px-4 py-3 md:px-6 md:py-4 shadow-sm">
        <button
          onClick={handleCancel}
          className="flex items-center text-gray-600 hover:text-gray-900 transition-colors min-h-[44px]"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span className="text-sm md:text-base font-medium">
            {profileShellCompte && isOwnAccount ? 'Retour au profil IAM' : 'Retour'}
          </span>
        </button>
      </div>

      <div className="max-w-4xl mx-auto px-3 py-4 md:px-6 md:py-8">
        {/* Header */}
        <div className="mb-4 md:mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">Modifier l'utilisateur</h1>
            <p className="text-xs sm:text-sm md:text-base text-gray-600 mt-1 md:mt-2">Modifiez les informations de l'utilisateur</p>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
          <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom d'utilisateur *
                </label>
                <input
                  type="text"
                  required
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                  placeholder="username"
                />
              </div>

              {!isOwnAccount && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom complet
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                    placeholder="Nom complet (optionnel)"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom d'utilisateur
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                  placeholder="Nom d'utilisateur"
                />
                {isOwnAccount && (
                  <p className="mt-1 text-xs text-gray-500">Au moins un identifiant requis (téléphone, username ou email)</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Téléphone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                  placeholder="+250788123456"
                />
                {!isOwnAccount && (
                  <p className="mt-1 text-xs text-gray-500">Au moins un identifiant requis (téléphone, username ou email)</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                  placeholder="email@example.com"
                />
                {formData.member_id && (
                  <p className="mt-1 text-xs text-entrea-royal">
                    ⓘ L'email sera automatiquement synchronisé avec le profil membre associé
                  </p>
                )}
              </div>

              {/* Mot de passe - visible pour tous */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {isOwnAccount ? 'Nouveau mot de passe' : 'Mot de passe'}
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                  placeholder={isOwnAccount ? 'Entrez un nouveau mot de passe' : 'Laisser vide pour ne pas changer'}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {isOwnAccount 
                    ? 'Laissez vide si vous ne souhaitez pas changer votre mot de passe' 
                    : 'Laisser vide pour conserver le mot de passe actuel'}
                </p>
              </div>

              {canEditRole && (
                <>
                  {editingTechnicalIam ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rôle IAM (console) *
                      </label>
                      <select
                        required
                        value={formData.role}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                      >
                        {assignableRoles.map((r) => (
                          <option key={r.key} value={r.key}>
                            {r.label}
                          </option>
                        ))}
                        {formData.role &&
                          !assignableRoles.some((r) => r.key === formData.role) && (
                            <option value={formData.role}>{formatIamRoleLabel(formData.role)}</option>
                          )}
                      </select>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-500">
                        Rôle IAM :{' '}
                        <span className="font-medium text-gray-800">{formatIamRoleLabel(formData.role)}</span>
                        {' — '}droits métier via <strong>position</strong> (console admin).
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Position projet</label>
                    {editingTechnicalIam ? (
                      <p className="text-xs text-gray-500 mb-1">
                        Optionnel pour un compte technique ayant aussi un usage métier sur ce projet.
                      </p>
                    ) : null}
                    <select
                      value={formData.position_id || ''}
                      onChange={(e) => {
                        const nextPid = e.target.value;
                        const derivedRole = nextPid
                          ? resolveUserRoleFromPosition(nextPid, projectPositions, formData.role || 'participant')
                          : null;
                        setFormData((prev) => ({
                          ...prev,
                          position_id: nextPid,
                          ...(derivedRole && !editingTechnicalIam ? { role: derivedRole } : {}),
                        }));
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                    >
                      <option value="">— Aucune —</option>
                      {projectPositions.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                    {(() => {
                      const pid = String(formData.position_id || '').trim();
                      if (!pid) return null;
                      const pos = projectPositions.find((p) => String(p?.id || '').trim() === pid);
                      const primary = primaryRoleKeyFromPosition(pos);
                      if (!primary) return null;
                      return (
                        <p className="mt-1 text-xs text-gray-500">
                          Cette position confère le rôle{' '}
                          <span className="font-semibold text-entrea-royal">
                            {formatIamRoleLabel(primary)}
                          </span>{' '}
                          <span className="font-mono text-gray-500">({primary})</span>.
                        </p>
                      );
                    })()}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Membre associé
                    </label>
                    <select
                      value={formData.member_id}
                      onChange={(e) => setFormData({ ...formData, member_id: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-entrea-royal/40 focus:border-transparent"
                    >
                      <option value="">Aucun membre associé</option>
                      {members.map((member) => (
                        <option key={member.id} value={member.id}>
                          {member.name} {member.email ? `(${member.email})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        className="rounded border-gray-300 text-entrea-royal focus:ring-entrea-royal/40"
                      />
                      <span className="text-sm font-medium text-gray-700">Compte actif</span>
                    </label>
                  </div>
                </>
              )}

              {!canEditRole ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Position projet</label>
                  <select
                    value={formData.position_id || ''}
                    disabled
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600"
                  >
                    <option value="">— Aucune —</option>
                    {projectPositions.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    Visible en lecture seule. Pour modifier la position, utilisez un compte admin Entrea.
                  </p>
                </div>
              ) : null}
            </div>

            {/* PIN Management Section - Only for own account */}
            {isOwnAccount && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Gestion du PIN</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Créez ou modifiez votre PIN à 6 chiffres pour l'authentification mobile
                </p>

                <div className="space-y-4">
                  {userHasPin && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
                        Ancien PIN
                      </label>
                      <PINInput
                        value={oldPin}
                        onChange={(value) => setOldPin(value)}
                        onComplete={(value) => handlePINComplete(value, 'old')}
                        disabled={savingPin}
                        autoFocus={false}
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
                      Nouveau PIN
                    </label>
                    <PINInput
                      value={pin}
                      onChange={(value) => setPin(value)}
                      onComplete={(value) => handlePINComplete(value, 'new')}
                      disabled={savingPin}
                      autoFocus={false}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
                      Confirmer le PIN
                    </label>
                    <PINInput
                      value={confirmPin}
                      onChange={(value) => setConfirmPin(value)}
                      onComplete={(value) => handlePINComplete(value, 'confirm')}
                      disabled={savingPin}
                      autoFocus={false}
                    />
                  </div>

                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={handleSavePIN}
                      disabled={savingPin || pin.replace(/\D/g, '').length !== 6 || confirmPin.replace(/\D/g, '').length !== 6 || (userHasPin && oldPin.replace(/\D/g, '').length !== 6)}
                      className="w-full py-3 px-4 bg-gradient-to-r from-entrea-ink to-entrea-royal text-white rounded-xl shadow-lg shadow-entrea-royal/25 hover:from-entrea-navy hover:to-entrea-royal transition disabled:opacity-50 disabled:cursor-not-allowed font-medium min-h-[44px]"
                    >
                      {savingPin ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sauvegarde...
                        </span>
                      ) : (
                        'Enregistrer le PIN'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 md:gap-4 pt-4 md:pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium text-sm"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-entrea-royal text-white rounded-lg hover:bg-entrea-navy transition font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center text-sm"
              >
                {saving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 md:mr-3 h-4 w-4 md:h-5 md:w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="hidden sm:inline">Enregistrement...</span>
                    <span className="sm:hidden">En cours...</span>
                  </>
                ) : (
                  <>
                    <span className="hidden sm:inline">Enregistrer les modifications</span>
                    <span className="sm:hidden">Enregistrer</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserEdit;

